import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, FileText } from 'lucide-react';
import { feePaymentClaimSchema } from '@bbl/validation';
import { z } from 'zod';
import { apiRequest, ApiError, openApiDocument } from '../lib/api';
import { useToast } from '../lib/toast';
import { useAuthStore } from '../lib/auth-store';
import { formatINR, formatDate } from '../lib/format';
import { ListShell } from '../components/ListShell';
import { Button } from '../components/ui/Button';
import { Drawer } from '../components/ui/Drawer';
import { EmptyState } from '../components/ui/EmptyState';
import { ErrorState } from '../components/ui/ErrorState';
import { Skeleton } from '../components/ui/Skeleton';
import { TextField, SelectField, TextAreaField } from '../components/ui/Field';
import { FeeStatusBadge } from '../components/finance/StatusBadge';
import {
  PAID_VIA_OPTIONS,
  cycleLabel,
  type FeeCycle,
  type FeePayment,
  type FeePaymentJoined,
  type FeePaymentStatus,
} from '../components/finance/types';

export const Route = createFileRoute('/_authed/me/fees')({
  component: MyFeesPage,
});

// Self-service fees. Reads `/api/me/fees`, which is role-gated to
// parent+student and already scoped server-side to the caller's own children
// (see `resolveSelfStudentIds`) — so there is no client-side filtering to do
// and no way to address someone else's invoice.
//
// This page previously called `/api/fees/payments` + `/api/fees/cycles`, both
// of which are gated to staff READ roles. Every parent and student therefore
// got a 403 and saw a "waiting on a backend update" card — while the endpoint
// it claimed to be waiting for already existed and the mobile app was using
// it. Covered by e2e/specs/02-parent-fees.spec.ts.

function MyFeesPage() {
  const user = useAuthStore((s) => s.user);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [claimFor, setClaimFor] = useState<FeePayment | null>(null);

  // Students with no household can't be linked to fees. Computed as a flag
  // rather than returned early: an early `return` above the hooks below
  // changes how many hooks this component calls between renders, which
  // crashes React with "rendered more hooks than during the previous render".
  //
  // NOT a mount-time concern — `_authed`'s `beforeLoad` awaits `api.me()` and
  // calls `setUser` before any child renders, so `user` is always populated on
  // render 1. The flag flips mid-mount, in two ways:
  //   - logout: `reset()` (auth-store) sets `user: null` synchronously while
  //     this page is still mounted, so the flag goes true -> false;
  //   - `householdId` is `string | null` and a later `setUser` can fill it in.
  // Either transition changed the hook count under the old early return.
  const studentWithoutHousehold =
    user?.primaryRole === 'student' && !user.householdId;

  // One request: `/api/me/fees` returns `{ cycles, payments }` already scoped
  // to this user's own students, so cycles and payments cannot disagree about
  // which rows the caller may see, and there is no second query to race.
  const feesQuery = useQuery({
    queryKey: ['me', 'fees'] as const,
    queryFn: async ({ signal }) => {
      const res = await apiRequest<{ cycles: FeeCycle[]; payments: unknown[] }>(
        '/api/me/fees',
        { signal },
      );
      return {
        cycles: res.cycles ?? [],
        rows: (res.payments ?? []).map(normaliseFeeRow),
      };
    },
    enabled: Boolean(user) && !studentWithoutHousehold,
    retry: false,
  });

  const filtered = useMemo(() => feesQuery.data?.rows ?? [], [feesQuery.data]);

  const cyclesById = useMemo(() => {
    const map = new Map<string, FeeCycle>();
    for (const c of feesQuery.data?.cycles ?? []) map.set(c.id, c);
    return map;
  }, [feesQuery.data]);

  // Group by cycle id.
  const grouped = useMemo(() => {
    const groups = new Map<string, FeePaymentJoined[]>();
    for (const row of filtered) {
      const existing = groups.get(row.payment.cycleId);
      if (existing) existing.push(row);
      else groups.set(row.payment.cycleId, [row]);
    }
    const entries: Array<{ cycle: FeeCycle | null; rows: FeePaymentJoined[] }> = [];
    for (const [cycleId, rows] of groups.entries()) {
      entries.push({ cycle: cyclesById.get(cycleId) ?? null, rows });
    }
    // Sort newest cycle first when cycle data is available, otherwise stable order.
    entries.sort((a, b) => {
      if (!a.cycle || !b.cycle) return 0;
      if (a.cycle.year !== b.cycle.year) return b.cycle.year - a.cycle.year;
      return b.cycle.month - a.cycle.month;
    });
    return entries;
  }, [filtered, cyclesById]);

  const openReceipt = async (paymentId: string) => {
    try {
      await openApiDocument(`/api/fees/payments/${encodeURIComponent(paymentId)}/receipt-url`);
    } catch (err) {
      if (err instanceof ApiError) toast(err.message, { variant: 'error' });
      else toast('Could not open receipt', { variant: 'error' });
    }
  };

  if (studentWithoutHousehold) {
    return (
      <ListShell title="My fees" description="Your monthly fee payments.">
        <EmptyState
          title="No fees linked to your account"
          description="Ask your parent to view fees from their account. If this looks wrong, please contact the academy office."
        />
      </ListShell>
    );
  }

  return (
    <ListShell title="My fees" description="Your monthly fee payments.">
      {feesQuery.error ? (
        <ErrorState
          title="Could not load your fees"
          description={
            feesQuery.error instanceof ApiError
              ? feesQuery.error.message
              : 'Please try again.'
          }
          onRetry={() => void feesQuery.refetch()}
        />
      ) : feesQuery.isLoading ? (
        <div className="space-y-4">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      ) : grouped.length === 0 ? (
        <EmptyState
          title="No fees yet"
          description="When the academy issues new fee records, they'll appear here grouped by month."
        />
      ) : (
        <div className="space-y-4">
          {grouped.map(({ cycle, rows }) => (
            <CycleCard
              key={cycle?.id ?? rows[0]?.payment.cycleId ?? 'unknown'}
              cycle={cycle}
              rows={rows}
              onClaim={(p) => setClaimFor(p)}
              onDownload={openReceipt}
            />
          ))}
        </div>
      )}

      <SubmitClaimDrawer
        payment={claimFor}
        onClose={() => setClaimFor(null)}
        onSubmitted={async () => {
          await queryClient.invalidateQueries({ queryKey: ['me', 'fees'] });
          toast('Claim submitted. The academy will confirm it shortly.', {
            variant: 'success',
          });
          setClaimFor(null);
        }}
      />
    </ListShell>
  );
}

// ---------- Cycle card ----------

interface CycleCardProps {
  cycle: FeeCycle | null;
  rows: FeePaymentJoined[];
  onClaim: (payment: FeePayment) => void;
  onDownload: (paymentId: string) => void;
}

function CycleCard({ cycle, rows, onClaim, onDownload }: CycleCardProps) {
  const totalNet = rows.reduce((sum, r) => sum + r.payment.netAmountPaise, 0);
  const allPaid = rows.every((r) => r.payment.status === 'paid');

  return (
    <section className="rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] overflow-hidden">
      <header className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between border-b border-[color:var(--color-border)] px-4 py-3 bg-[color:var(--color-surface)]">
        <div className="min-w-0">
          <h3 className="font-display font-semibold text-base">
            {cycle ? cycleLabel(cycle.year, cycle.month) : 'Cycle'}
          </h3>
          {cycle ? (
            <p className="text-xs text-[color:var(--color-text-muted)]">
              Due {formatDate(`${cycle.dueDate}T00:00:00Z`)}
            </p>
          ) : null}
        </div>
        <div className="text-sm">
          <span className="text-[color:var(--color-text-muted)] mr-1">Total</span>
          <span className="font-semibold">{formatINR(totalNet)}</span>
          {allPaid ? (
            <span className="ml-3">
              <FeeStatusBadge status="paid" />
            </span>
          ) : null}
        </div>
      </header>
      <ul className="divide-y divide-[color:var(--color-border)]" role="list">
        {rows.map((r) => (
          <PaymentRow key={r.payment.id} row={r} onClaim={onClaim} onDownload={onDownload} />
        ))}
      </ul>
    </section>
  );
}

interface PaymentRowProps {
  row: FeePaymentJoined;
  onClaim: (payment: FeePayment) => void;
  onDownload: (paymentId: string) => void;
}

function PaymentRow({ row, onClaim, onDownload }: PaymentRowProps) {
  const p = row.payment;
  const canClaim: FeePaymentStatus[] = ['pending', 'overdue', 'partial'];
  return (
    <li className="px-4 py-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <p className="font-medium truncate">{row.student.name}</p>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
          <FeeStatusBadge status={p.status} />
          <span className="text-[color:var(--color-text-muted)]">
            Net {formatINR(p.netAmountPaise)}
          </span>
          {p.discountPaise > 0 ? (
            <span className="text-[color:var(--color-text-muted)]">
              · Discount {formatINR(p.discountPaise)}
            </span>
          ) : null}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {p.status === 'paid' ? (
          <Button
            size="sm"
            variant="secondary"
            icon={<Download size={14} />}
            onClick={() => onDownload(p.id)}
          >
            Download receipt
          </Button>
        ) : canClaim.includes(p.status) ? (
          <Button
            size="sm"
            icon={<FileText size={14} />}
            onClick={() => onClaim(p)}
          >
            Submit payment claim
          </Button>
        ) : null}
      </div>
    </li>
  );
}

// ---------- Submit claim drawer ----------

const claimFormSchema = z.object({
  claimedPaidVia: z.enum(['cash', 'bank_transfer', 'upi_offline', 'cheque', 'other']),
  claimedPaidAt: z.string().min(1, 'Required'),
  reference: z.string().max(200).optional(),
  notes: z.string().max(500).optional(),
});
type ClaimFormValues = z.infer<typeof claimFormSchema>;

interface SubmitClaimDrawerProps {
  payment: FeePayment | null;
  onClose: () => void;
  onSubmitted: () => Promise<void> | void;
}

function SubmitClaimDrawer({ payment, onClose, onSubmitted }: SubmitClaimDrawerProps) {
  const { toast } = useToast();
  const open = !!payment;
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ClaimFormValues>({
    resolver: zodResolver(claimFormSchema),
    defaultValues: {
      claimedPaidVia: 'upi_offline',
      claimedPaidAt: new Date().toISOString().slice(0, 16),
    },
  });

  useEffect(() => {
    if (open) {
      reset({
        claimedPaidVia: 'upi_offline',
        claimedPaidAt: new Date().toISOString().slice(0, 16),
        reference: '',
        notes: '',
      });
    }
  }, [open, reset]);

  const mutation = useMutation({
    mutationFn: async (body: ClaimFormValues) => {
      if (!payment) throw new Error('No payment selected');
      const parsed = feePaymentClaimSchema.parse({
        paymentId: payment.id,
        claimedPaidVia: body.claimedPaidVia,
        claimedPaidAt: new Date(body.claimedPaidAt).toISOString(),
        ...(body.reference ? { reference: body.reference } : {}),
        ...(body.notes ? { notes: body.notes } : {}),
      });
      // `/api/me/fees/claims`, not `/api/fees/claims`: the self-service route
      // is gated to parent+student (the staff one is parent-only, so a student
      // with an invoice would hit a 403 on a button they can see) and it
      // re-verifies that the invoice belongs to the caller.
      await apiRequest<{ id: string }>('/api/me/fees/claims', {
        method: 'POST',
        body: parsed,
      });
    },
    onSuccess: () => onSubmitted(),
    onError: (err) => {
      if (err instanceof ApiError) toast(err.message, { variant: 'error' });
      else toast('Could not submit claim.', { variant: 'error' });
    },
  });

  return (
    <Drawer open={open} onClose={onClose} title="Submit payment claim" size="md">
      {payment ? (
        <form
          onSubmit={handleSubmit((v) => mutation.mutate(v))}
          className="space-y-4"
          noValidate
        >
          <div className="rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-3 py-2 text-sm space-y-1">
            <div className="flex justify-between gap-2">
              <span className="text-[color:var(--color-text-muted)]">Net amount</span>
              <span className="font-semibold">
                {formatINR(payment.netAmountPaise)}
              </span>
            </div>
            <div className="flex justify-between gap-2">
              <span className="text-[color:var(--color-text-muted)]">Due</span>
              <span>{formatDate(`${payment.dueDate}T00:00:00Z`)}</span>
            </div>
          </div>
          <p className="text-xs text-[color:var(--color-text-muted)]">
            Tell us how and when you paid. The academy will verify and confirm
            your payment, after which you'll receive a receipt.
          </p>
          <SelectField
            label="How did you pay?"
            {...register('claimedPaidVia')}
            error={errors.claimedPaidVia?.message}
            options={PAID_VIA_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
          />
          <TextField
            label="When did you pay?"
            type="datetime-local"
            {...register('claimedPaidAt')}
            error={errors.claimedPaidAt?.message}
          />
          <TextField
            label="Reference"
            optional
            placeholder="e.g. UPI transaction ID, cheque number"
            {...register('reference')}
            error={errors.reference?.message}
          />
          <TextAreaField
            label="Notes"
            optional
            placeholder="Anything else we should know"
            {...register('notes')}
            error={errors.notes?.message}
          />
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={isSubmitting || mutation.isPending}>
              Submit claim
            </Button>
          </div>
        </form>
      ) : null}
    </Drawer>
  );
}

// ---------- Helpers ----------

function normaliseFeeRow(raw: unknown): FeePaymentJoined {
  if (raw && typeof raw === 'object' && 'payment' in raw && 'student' in raw) {
    return raw as FeePaymentJoined;
  }
  const row = raw as Record<string, unknown>;
  const payment: FeePayment = {
    id: String(row.id ?? ''),
    cycleId: String(row.cycleId ?? row.cycle_id ?? ''),
    studentId: String(row.studentId ?? row.student_id ?? ''),
    amountPaise: Number(row.amountPaise ?? row.amount_paise ?? 0),
    discountPaise: Number(row.discountPaise ?? row.discount_paise ?? 0),
    netAmountPaise: Number(row.netAmountPaise ?? row.net_amount_paise ?? 0),
    dueDate: String(row.dueDate ?? row.due_date ?? ''),
    status: (row.status as FeePaymentStatus) ?? 'pending',
    paidAt: (row.paidAt ?? row.paid_at ?? null) as string | null,
    paidVia: (row.paidVia ?? row.paid_via ?? null) as FeePayment['paidVia'],
    paidReference: (row.paidReference ?? row.paid_reference ?? null) as string | null,
    receiptNumber: (row.receiptNumber ?? row.receipt_number ?? null) as string | null,
    provider: (row.provider as FeePayment['provider']) ?? 'manual',
    providerOrderId: (row.providerOrderId ?? row.provider_order_id ?? null) as string | null,
    providerPaymentId: (row.providerPaymentId ?? row.provider_payment_id ?? null) as
      | string
      | null,
    providerSignature: (row.providerSignature ?? row.provider_signature ?? null) as
      | string
      | null,
    providerPaidAt: (row.providerPaidAt ?? row.provider_paid_at ?? null) as string | null,
    notes: (row.notes ?? null) as string | null,
    confirmedBy: (row.confirmedBy ?? row.confirmed_by ?? null) as string | null,
    createdAt: String(row.createdAt ?? row.created_at ?? ''),
    updatedAt: String(row.updatedAt ?? row.updated_at ?? ''),
  };
  return {
    payment,
    student: {
      id: payment.studentId,
      name: String(row.studentName ?? row.student_name ?? '—'),
      admissionNumber: (row.admissionNumber ?? row.admission_number ?? null) as
        | string
        | null,
      householdId: (row.householdId ?? row.household_id ?? null) as string | null,
    },
  };
}
